import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LabelList, ResponsiveContainer,
} from 'recharts';
import { getPlayers, getOpponents, getScenarios, getLoadScenarios, getMatchDrills, getGPS, getPlayerPositions } from '../../api';
import { useSeason, dateToSeason } from '../../SeasonContext';
import { useUrlParam } from '../../hooks/useUrlParam';
import {
  f0, f1, fPct, fmtDate, computePerMinRow, computeMatchRows, computeTeamMatchRows,
} from './matchChronicShared';
import MultiSelect from '../../components/MultiSelect';
import { EstimatedValue, EstimatedMarker } from '../../components/EstimatedMarker';
import PhysicalReportExportModal from './PhysicalReportExportModal';

// Clickable, sortable column header — same visual pattern (ArrowUpDown/Up/Down from
// lucide-react) as every other sortable table in this codebase.
function SortTh({ children, sortKey: key, activeKey, dir, onClick, style }) {
  const active = activeKey === key;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th onClick={() => onClick(key)} style={{ ...style, cursor: 'pointer', userSelect: 'none', color: active ? 'var(--text-primary)' : style.color }}>
      {children} <Icon size={10} style={{ verticalAlign: 'middle', marginLeft: 2 }} />
    </th>
  );
}

// Same NULL-to-bottom-regardless-of-direction comparator as Team360Table.js (Stage 4) and
// every other sortable table in this codebase (TeamKpiOverview.js, TechnicalOverview.js) —
// each keeps its own copy rather than a shared util (existing convention).
function compareRows(a, b, key, dir) {
  const av = a[key], bv = b[key];
  const aNull = av === null || av === undefined;
  const bNull = bv === null || bv === undefined;
  if (aNull && bNull) return 0;
  if (aNull) return 1;
  if (bNull) return -1;
  if (typeof av === 'string') return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  return dir === 'asc' ? av - bv : bv - av;
}

// ── Bar chart metric picker (2026-07-25) — 'combo' is the original fixed HID+Explosive
// stacked view; anything else plots that one field as a single flat bar. 'expl' is included
// in the field list (computed for every row) even though it's only shown standalone or as
// half of 'combo', so switching between the two never needs a different data shape.
const CHART_METRIC_FIELDS = ['td', 'hid', 'sd', 'hmld', 'sa', 'expl'];
const CHART_METRIC_OPTIONS = [
  { value: 'combo', label: 'HID + Explosive' },
  { value: 'td',    label: 'Total Distance' },
  { value: 'hid',   label: 'High Intensity Distance' },
  { value: 'sd',    label: 'Sprint Distance' },
  { value: 'hmld',  label: 'High Metabolic Load Distance' },
  { value: 'sa',    label: 'Short Accelerations' },
  { value: 'expl',  label: 'Explosive Distance' },
];
const CHART_METRIC_COLORS = { td: '#22c55e', hid: '#38bdf8', sd: '#f59e0b', hmld: '#a855f7', sa: '#ef4444', expl: '#1e40af' };
// TD/min is a big-enough number (~100+ m/min) that a decimal just adds noise; the others are
// small per-minute values (single/low-double digits) where the decimal matters for legibility.
const CHART_METRIC_PERMIN_DECIMALS = { td: 0, hid: 1, sd: 1, hmld: 1, sa: 1, expl: 1 };

// ── Fixed A4 PDF template (794×1123 px, white, light theme always) ─────────────
function PDFTemplate({ player, matchRows, clubLogoUrl }) {
  const MAX_ROWS = 12;
  const displayRows = matchRows.slice(0, MAX_ROWS);
  const truncated   = matchRows.length > MAX_ROWS;
  const fontSize    = matchRows.length <= 8 ? 11 : 9;

  const n = displayRows.length;
  // Null-aware (2026-10-24): a KNVB-estimated match without this metric is left out of its average.
  const avg = (key) => {
    const vals = displayRows.map(r => r[key]).filter(v => v != null);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  };

  const avgs  = n > 0 ? { mins: avg('mins'), td: avg('td'), hid: avg('hid'), sa: avg('sa') } : null;
  const avgRow = n > 0 ? {
    td: avg('td'), hid: avg('hid'), sd: avg('sd'), hmld: avg('hmld'),
    sa: avg('sa'), maxSpd: avg('maxSpd'), mins: avg('mins'),
    tdPct: avg('tdPct'), hidPct: avg('hidPct'), sdPct: avg('sdPct'),
    hmldPct: avg('hmldPct'), saPct: avg('saPct'),
  } : null;
  const perMinRow = computePerMinRow(displayRows);

  // Bar chart: last 4 matches
  const chartData = matchRows.slice(0, 4).map(r => ({
    label: r.opponent.split(' ').slice(0, 2).join(' '),
    date:  r.date ? `${r.date.slice(8)}-${r.date.slice(5, 7)}` : '',
    hid:   Math.round(r.hid),
    expl:  Math.round(r.expl),
  })).reverse();

  // Palette — always light
  const CLR = {
    bg: '#ffffff', bgCard: '#f8fafc', bgApp: '#f1f5f9',
    text: '#0f172a', muted: '#64748b', dimmed: '#94a3b8',
    border: '#e2e8f0', accent: '#2563eb',
    green: { background: 'rgba(16,185,129,0.12)', color: '#065f46' },
  };

  const PAD    = 24;
  const CARD_W = 190;
  const INNER  = 794 - PAD * 2; // 746
  const CHART_CONTAINER_W = INNER - CARD_W - 12;   // 544
  const CHART_W = CHART_CONTAINER_W - 24;           // 520 (12px pad each side)

  const TH = {
    padding: `5px 7px`, fontSize: Math.round(fontSize * 0.82),
    fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
    color: CLR.muted, textAlign: 'right', borderBottom: `1px solid ${CLR.border}`,
    whiteSpace: 'nowrap', background: CLR.bgApp,
  };
  const THL = { ...TH, textAlign: 'left' };
  const TD_ = {
    padding: `4px 7px`, fontSize,
    color: CLR.text, textAlign: 'right', borderBottom: `1px solid ${CLR.border}`,
  };
  const TDL = { ...TD_, textAlign: 'left' };

  const initials = (name) => (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div style={{
      width: 794, height: 1123, background: CLR.bg, color: CLR.text,
      fontFamily: 'Inter, Arial, sans-serif', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      padding: PAD, boxSizing: 'border-box',
    }}>

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        paddingBottom: 10, marginBottom: 12,
        borderBottom: `2px solid ${CLR.accent}`, flexShrink: 0,
      }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: CLR.muted, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Match Chronic
          </div>
          <div style={{ fontSize: 20, fontWeight: 800, color: CLR.text, marginTop: 2 }}>
            {player?.name || '—'}
          </div>
        </div>
        {clubLogoUrl && (
          <img src={clubLogoUrl} alt="Club" crossOrigin="anonymous"
            style={{ height: 44, width: 'auto', objectFit: 'contain' }} />
        )}
      </div>

      {/* ── Player card + bar chart ── */}
      {/* All heights are explicit px — no flex:1 so off-screen layout always resolves */}
      <div style={{ display: 'flex', gap: 12, height: 260, marginBottom: 12, flexShrink: 0 }}>

        {/* Player card — explicit column, no flex:1 anywhere */}
        <div style={{
          width: CARD_W, height: 260, background: CLR.bgCard, border: `1px solid ${CLR.border}`,
          borderRadius: 10, padding: '16px 12px 12px', display: 'flex', flexDirection: 'column',
          alignItems: 'center', gap: 8, flexShrink: 0, boxSizing: 'border-box',
          overflow: 'hidden',
        }}>
          {/* Avatar — 80px for a natural, non-cramped look */}
          {player?.photo_url ? (
            <img src={player.photo_url} alt={player.name} crossOrigin="anonymous"
              style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover',
                objectPosition: 'center top', border: `3px solid ${CLR.border}`,
                flexShrink: 0, display: 'block' }} />
          ) : (
            <div style={{
              width: 80, height: 80, borderRadius: '50%', background: CLR.accent, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 26, color: '#fff',
            }}>
              {initials(player?.name)}
            </div>
          )}
          <div style={{ textAlign: 'center', flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: CLR.text, lineHeight: 1.2 }}>
              {player?.name || '—'}
            </div>
            {player?.position && (
              <span style={{
                fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 99,
                background: 'rgba(37,99,235,0.12)', color: '#1e40af',
                letterSpacing: '0.06em', display: 'inline-block', marginTop: 5,
              }}>
                {player.position}
              </span>
            )}
          </div>
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
            {[
              { label: 'Avg Minutes',    value: avgs ? f1(avgs.mins) : '—' },
              { label: 'Avg Total Dist', value: avgs ? f0(avgs.td) + ' m' : '—' },
              { label: 'Avg HID',        value: avgs ? f0(avgs.hid) + ' m' : '—' },
              { label: 'Avg Short Act',  value: avgs ? f0(avgs.sa) : '—' },
            ].map(({ label, value }) => (
              <div key={label} style={{
                background: CLR.bgApp, borderRadius: 5, padding: '4px 8px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 9, color: CLR.muted }}>{label}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: CLR.text }}>{value}</span>
              </div>
            ))}
            <div style={{ fontSize: 9, color: CLR.dimmed, textAlign: 'center', marginTop: 2 }}>
              {n} match{n !== 1 ? 'es' : ''}
            </div>
          </div>
        </div>

        {/* Bar chart */}
        {(() => {
          const BAR_H  = 120; // height of bar drawing area
          const LABEL_H = 32; // x-axis label strip
          const BAR_W  = 52;  // each bar is fixed 52px — like recharts maxBarSize
          const maxVal = chartData.length > 0 ? Math.max(1, ...chartData.map(d => d.hid + d.expl)) : 1;
          return (
            <div style={{
              flex: 1, height: 260, background: CLR.bgCard, border: `1px solid ${CLR.border}`,
              borderRadius: 10, padding: 12, boxSizing: 'border-box', overflow: 'hidden',
            }}>
              {/* Title row */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: CLR.text }}>
                  Game Load{' '}
                  <span style={{ fontSize: 9, fontWeight: 400, color: CLR.muted }}>
                    last {chartData.length} match{chartData.length !== 1 ? 'es' : ''}
                  </span>
                </span>
                <div style={{ display: 'flex', gap: 12, fontSize: 8, color: CLR.muted }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: '#38bdf8', display: 'inline-block' }} />
                    HID &gt;19.8 km/h
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: '#1e40af', display: 'inline-block' }} />
                    Explosive Dist.
                  </span>
                </div>
              </div>

              {chartData.length > 0 ? (
                <>
                  {/* Bar drawing area — explicit height, bars centered in columns */}
                  <div style={{ height: BAR_H, position: 'relative', display: 'flex', alignItems: 'flex-end' }}>
                    {/* 50% and 100% gridlines */}
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: BAR_H, borderTop: `1px dashed ${CLR.border}` }} />
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: BAR_H * 0.5, borderTop: `1px dashed ${CLR.border}` }} />
                    {/* Baseline */}
                    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, background: CLR.border }} />
                    {/* One flex:1 column per match, bar centered inside at fixed BAR_W */}
                    {chartData.map((d, idx) => {
                      const total = d.hid + d.expl;
                      const barH  = total > 0 ? Math.max(4, Math.round((total / maxVal) * BAR_H)) : 4;
                      const explH = total > 0 ? Math.round((d.expl / total) * barH) : 0;
                      const hidH  = barH - explH;
                      return (
                        <div key={idx} style={{ flex: 1, height: BAR_H, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                          <div style={{ width: BAR_W, height: barH, display: 'flex', flexDirection: 'column', borderRadius: '3px 3px 0 0', overflow: 'hidden' }}>
                            <div style={{ height: explH, background: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {explH >= 16 && <span style={{ fontSize: 7, color: '#fff', fontWeight: 700 }}>{(d.expl/1000).toFixed(1)}k</span>}
                            </div>
                            <div style={{ height: hidH, background: '#38bdf8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              {hidH >= 16 && <span style={{ fontSize: 7, color: '#fff', fontWeight: 700 }}>{(d.hid/1000).toFixed(1)}k</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {/* X-axis labels */}
                  <div style={{ height: LABEL_H, display: 'flex', paddingTop: 6 }}>
                    {chartData.map((d, idx) => (
                      <div key={idx} style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 8, color: CLR.muted, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{d.label}</div>
                        <div style={{ fontSize: 7, color: CLR.dimmed, marginTop: 2 }}>{d.date}</div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div style={{ height: BAR_H + LABEL_H, display: 'flex', alignItems: 'center', justifyContent: 'center', color: CLR.dimmed, fontSize: 11 }}>
                  No match data
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* ── Match history table ── */}
      <div style={{
        flex: 1, background: CLR.bgCard, border: `1px solid ${CLR.border}`,
        borderRadius: 10, overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '7px 12px', borderBottom: `1px solid ${CLR.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: CLR.text }}>Match History</span>
          <span style={{ fontSize: 9, color: CLR.muted }}>
            {truncated
              ? `Showing last ${MAX_ROWS} of ${matchRows.length} matches`
              : `${n} match${n !== 1 ? 'es' : ''}`}
          </span>
        </div>
        <div style={{ overflow: 'hidden', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={THL}>Opponent</th>
                <th style={TH}>Date</th>
                <th style={TH}>TD</th>
                <th style={TH}>TD%</th>
                <th style={TH}>HID</th>
                <th style={TH}>HID%</th>
                <th style={TH}>SD</th>
                <th style={TH}>SD%</th>
                <th style={TH}>HMLD</th>
                <th style={TH}>HMLD%</th>
                <th style={TH}>SA</th>
                <th style={TH}>SA%</th>
                <th style={TH}>Max Spd</th>
                <th style={TH}>Mins</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r, i) => (
                <tr key={r.sessionId} style={{ background: i % 2 !== 0 ? CLR.bgApp : 'transparent' }}>
                  <td style={TDL}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      {r.oppLogo ? (
                        <img src={r.oppLogo} alt={r.opponent} crossOrigin="anonymous"
                          style={{ width: 13, height: 13, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }} />
                      ) : (
                        <div style={{
                          width: 13, height: 13, borderRadius: 3, background: '#dbeafe', flexShrink: 0,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 7, fontWeight: 700, color: '#1e40af',
                        }}>
                          {(r.opponent || '?')[0]}
                        </div>
                      )}
                      <span style={{ fontWeight: 600 }}>{r.opponent}</span>
                      {r.isEstimated && <EstimatedMarker basis={r.estimate} />}
                    </div>
                  </td>
                  <td style={{ ...TD_, whiteSpace: 'nowrap', color: CLR.muted }}>{fmtDate(r.date)}</td>
                  <td style={{ ...TD_, ...(r.tdGreen   ? CLR.green : {}) }}><EstimatedValue value={r.td} basis={r.estimate} metric="td" /></td>
                  <td style={{ ...TD_, ...(r.tdGreen   ? CLR.green : {}) }}>{fPct(r.tdPct)}</td>
                  <td style={{ ...TD_, ...(r.hidGreen  ? CLR.green : {}) }}><EstimatedValue value={r.hid} basis={r.estimate} metric="hid" /></td>
                  <td style={{ ...TD_, ...(r.hidGreen  ? CLR.green : {}) }}>{fPct(r.hidPct)}</td>
                  <td style={{ ...TD_, ...(r.sdGreen   ? CLR.green : {}) }}><EstimatedValue value={r.sd} basis={r.estimate} metric="sd" /></td>
                  <td style={{ ...TD_, ...(r.sdGreen   ? CLR.green : {}) }}>{fPct(r.sdPct)}</td>
                  <td style={{ ...TD_, ...(r.hmldGreen ? CLR.green : {}) }}><EstimatedValue value={r.hmld} basis={r.estimate} metric="hmld" /></td>
                  <td style={{ ...TD_, ...(r.hmldGreen ? CLR.green : {}) }}>{fPct(r.hmldPct)}</td>
                  <td style={{ ...TD_, ...(r.saGreen   ? CLR.green : {}) }}><EstimatedValue value={r.sa} basis={r.estimate} metric="sa" /></td>
                  <td style={{ ...TD_, ...(r.saGreen   ? CLR.green : {}) }}>{fPct(r.saPct)}</td>
                  <td style={TD_}><EstimatedValue value={r.maxSpd} basis={r.estimate} metric="max_speed" format={v => v.toFixed(1)} /></td>
                  <td style={TD_}>{f1(r.mins)}</td>
                </tr>
              ))}
              {avgRow && (
                <tr style={{ background: 'rgba(37,99,235,0.06)', borderTop: `2px solid ${CLR.border}` }}>
                  <td style={{ ...TDL, fontWeight: 700, color: CLR.muted }}>Season Average</td>
                  <td style={TD_}>—</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.td)}</td>
                  <td style={TD_}>{fPct(avgRow.tdPct)}</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.hid)}</td>
                  <td style={TD_}>{fPct(avgRow.hidPct)}</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.sd)}</td>
                  <td style={TD_}>{fPct(avgRow.sdPct)}</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.hmld)}</td>
                  <td style={TD_}>{fPct(avgRow.hmldPct)}</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f0(avgRow.sa)}</td>
                  <td style={TD_}>{fPct(avgRow.saPct)}</td>
                  <td style={TD_}>{f1(avgRow.maxSpd)}</td>
                  <td style={TD_}>{f1(avgRow.mins)}</td>
                </tr>
              )}
              {perMinRow && (
                <tr style={{ background: 'rgba(37,99,235,0.06)' }}>
                  <td style={{ ...TDL, fontWeight: 700, color: CLR.muted, fontSize: Math.round(fontSize * 0.9) }}>Avg / Min Played</td>
                  <td style={TD_}>—</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f0(perMinRow.td)}</td>
                  <td style={TD_}>—</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.hid)}</td>
                  <td style={TD_}>—</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.sd)}</td>
                  <td style={TD_}>—</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.hmld)}</td>
                  <td style={TD_}>—</td>
                  <td style={{ ...TD_, fontWeight: 700 }}>{f1(perMinRow.sa)}</td>
                  <td style={TD_}>—</td>
                  <td style={TD_}>—</td>
                  <td style={TD_}>—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Multi-select match dropdown — options are { value, label } objects ──────────
// ── Player selection modal for PDF export ─────────────────────────────────────
function ExportModal({ players, onExport, onClose }) {
  const all = players.map(p => String(p.id));
  const [sel, setSel] = useState(all);
  const toggle = id => setSel(s => s.includes(id) ? s.filter(v => v !== id) : [...s, id]);
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: 460, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 40px 80px rgba(0,0,0,0.6)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 22px 14px', borderBottom: '1px solid var(--border)' }}>
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>Export PDF</div>
            <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 2 }}>One page per player — select players to include</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
        </div>
        <div style={{ padding: '10px 22px 8px', borderBottom: '1px solid var(--border)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.83rem', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={sel.length === all.length} onChange={() => setSel(sel.length === all.length ? [] : all)} style={{ accentColor: '#3b82f6' }} />
            Select all ({all.length} players)
          </label>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, padding: '6px 22px 12px' }}>
          {players.map(p => (
            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}>
              <input type="checkbox" checked={sel.includes(String(p.id))} onChange={() => toggle(String(p.id))} style={{ accentColor: '#3b82f6' }} />
              <span style={{ flex: 1, fontSize: '0.84rem', color: 'var(--text-primary)', fontWeight: 500 }}>{p.name}</span>
              {p.position && <span style={{ fontSize: '0.72rem', color: 'var(--text-dimmed)' }}>{p.position}</span>}
            </label>
          ))}
        </div>
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.83rem', fontFamily: 'inherit' }}>Cancel</button>
          <button disabled={!sel.length} onClick={() => onExport(sel)}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', fontFamily: 'inherit', fontSize: '0.83rem', fontWeight: 600, cursor: sel.length ? 'pointer' : 'not-allowed',
              background: sel.length ? '#2563eb' : 'var(--border)', color: sel.length ? '#fff' : 'var(--text-dimmed)' }}>
            Export {sel.length} player{sel.length !== 1 ? 's' : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────────────────────
function Avatar({ name, photoUrl, size = 80 }) {
  const [err, setErr] = React.useState(false);
  const initials = name ? name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?';
  const colors = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444','#06b6d4','#ec4899'];
  const color = colors[(name?.charCodeAt(0) || 0) % colors.length];
  if (photoUrl && !err) return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0,
      backgroundImage: `url(${photoUrl})`, backgroundSize: 'cover', backgroundPosition: 'center top',
      border: '2px solid var(--border-strong)' }}>
      <img src={photoUrl} alt="" onError={() => setErr(true)} style={{ display: 'none' }} />
    </div>
  );
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', flexShrink: 0, background: color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: size * 0.32, color: '#fff', border: '2px solid var(--border-strong)' }}>
      {initials}
    </div>
  );
}

// ── Opponent logo ─────────────────────────────────────────────────────────────
function OppLogo({ name, logoUrl, size = 20 }) {
  const [err, setErr] = React.useState(false);
  if (logoUrl && !err) return (
    <img src={logoUrl} alt={name} onError={() => setErr(true)}
      style={{ width: size, height: size, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} />
  );
  return (
    <div style={{ width: size, height: size, borderRadius: 4, background: '#1e3a5f', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.45, fontWeight: 700, color: '#60a5fa' }}>
      {(name || '?')[0]}
    </div>
  );
}

// ── Bar chart label (skip if segment too short) ────────────────────────────────
// decimals=1 for the per-minute view (2026-07-25) — those values are single/low-double
// digits, so rounding to a whole number the way the volume view does would flatten most
// matches to indistinguishable labels.
function BarLabel({ x, y, width, height, value, decimals = 0 }) {
  if (!value || Math.abs(height) < 16) return null;
  return (
    <text x={x + width / 2} y={y + height / 2 + 4} textAnchor="middle"
      fill="#fff" fontSize={9} fontWeight="600" fontFamily="Inter, sans-serif">
      {decimals ? value.toFixed(decimals) : Math.round(value)}
    </text>
  );
}

// ── Two-option pill toggle (2026-07-25) — shared by the Individual/Team and Volume/Per-Minute
// switches below.
function SegmentedToggle({ options, value, onChange }) {
  return (
    <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 9, background: 'var(--bg-app)', border: '1px solid var(--border-strong)' }}>
      {options.map(opt => {
        const active = value === opt.value;
        return (
          <button key={opt.value} type="button" onClick={() => onChange(opt.value)}
            style={{
              padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
              background: active ? '#2563eb' : 'transparent',
              color: active ? '#fff' : 'var(--text-secondary)',
            }}>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MatchChronic({ playerOverride = null }) {
  const { selectedSeasons } = useSeason();
  const [players, setPlayers]             = useState([]);
  const [opponents, setOpponents]         = useState([]);
  const [scenarios, setScenarios]         = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [positions, setPositions]         = useState([]);
  const [playerId, setPlayerId]           = useUrlParam('player_id', playerOverride ? String(playerOverride.id) : '');
  const [allMatchDrills, setAllMatchDrills] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filterTitles, setFilterTitles]   = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showPhysicalReportModal, setShowPhysicalReportModal] = useState(false);
  // 'individual' | 'team' — toggle added 2026-07-25 alongside the per-minute stat toggle below.
  const [viewMode, setViewMode]           = useState('individual');
  // 'volume' | 'perMin'
  const [statMode, setStatMode]           = useState('volume');
  // Which parameter(s) the bar chart plots — 'combo' is the original HID+Explosive stack.
  const [chartMetric, setChartMetric]     = useState('combo');

  // ── Initial data load ───────────────────────────────────────────────────────
  // Fetches every player's match drills up front (getMatchDrills, same source
  // MatchPerformance.js uses) instead of one getPlayerMatchDrills call per player
  // selection — needed anyway for Team mode's squad-wide average, and it also means
  // switching players/modes is instant (no per-switch loading spinner). getMatchDrills'
  // rows don't carry session_date/title/opponent_id (unlike getPlayerMatchDrills), so
  // they're enriched here from getGPS()'s session list, keyed by session_id.
  useEffect(() => {
    Promise.all([getPlayers(), getOpponents(), getScenarios(), getLoadScenarios(), getMatchDrills(), getGPS(), getPlayerPositions()])
      .then(([pl, opps, scens, ct, drills, sessions, posHistory]) => {
        const sorted = (pl || []).filter(p => p.active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setPlayers(sorted);
        setOpponents(opps || []);
        setScenarios((scens || []).map(s => ({ ...s, startDate: s.start_date?.slice(0, 10), playerId: s.player_id })));
        setCustomTemplates(ct || []);
        setPositions((posHistory || []).map(p => ({ ...p, startDate: p.start_date?.slice(0, 10), playerId: p.player_id })));
        const sessionById = {};
        (sessions || []).forEach(s => { sessionById[s.id] = s; });
        setAllMatchDrills((drills || []).map(d => {
          const s = sessionById[d.session_id];
          return { ...d, session_date: s?.session_date, session_title: s?.session_title, opponent_id: s?.opponent_id };
        }));
        // Bug fix (2026-09-03): used to default to the first sorted player UNCONDITIONALLY,
        // discarding a `player_id` already in the URL — see PlayerChronic.js's identical fix
        // (window.location.search read directly, not the closure-captured useUrlParam binding —
        // that alone still showed the wrong player live; see that file's comment for why).
        const urlPlayerId = new URLSearchParams(window.location.search).get('player_id');
        if (playerOverride) setPlayerId(String(playerOverride.id)); else if (!urlPlayerId && sorted.length) setPlayerId(String(sorted[0].id));
      })
      .finally(() => setLoading(false));
  }, []);

  const player = useMemo(() => players.find(p => String(p.id) === playerId), [players, playerId]);

  const playerDrills = useMemo(() => {
    if (!player?.name) return [];
    const nameLower = player.name.toLowerCase();
    return allMatchDrills.filter(d => (d.player_name || '').toLowerCase() === nameLower);
  }, [allMatchDrills, player]);

  // ── Per-match rows — individual (this player) or team (squad average per match) ────────
  const individualMatchRows = useMemo(() =>
    computeMatchRows(playerDrills, player, opponents, scenarios, customTemplates, positions),
    [playerDrills, player, opponents, scenarios, customTemplates, positions]);

  const teamMatchRows = useMemo(() =>
    computeTeamMatchRows(allMatchDrills, players, opponents, scenarios, customTemplates, positions),
    [allMatchDrills, players, opponents, scenarios, customTemplates, positions]);

  const allMatchRows = viewMode === 'team' ? teamMatchRows : individualMatchRows;

  // ── Season-filtered rows (base for dropdown options and filtered view) ─────────
  const seasonRows = useMemo(() =>
    allMatchRows.filter(r => {
      if (!r.date) return true;
      const season = dateToSeason(r.date);
      return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
    }),
    [allMatchRows, selectedSeasons]);

  // Reset match filter when the season selection changes — the previously-selected match
  // session IDs may no longer even be in range. Deliberately NOT keyed on seasonRows (which
  // also changes on every player switch, since individualMatchRows depends on the selected
  // player) — a trainer comparing the same match across several players shouldn't have to
  // re-pick it every time they change the player dropdown (confirmed 2026-09-21). View-mode
  // switches (individual/team) reset filterTitles explicitly at their own onChange below,
  // since the two modes' session sets are unrelated.
  useEffect(() => { setFilterTitles([]); }, [selectedSeasons]);

  // ── Filters ─────────────────────────────────────────────────────────────────
  const opponentOptions = useMemo(() =>
    [...seasonRows]
      .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
      .map(r => ({
        value: String(r.sessionId),
        label: `${r.opponent}${r.date ? ` · ${r.date.slice(8)}/${r.date.slice(5, 7)}/${r.date.slice(2, 4)}` : ''}`,
      })),
    [seasonRows]);

  const filteredRows = useMemo(() => seasonRows.filter(r => {
    if (filterTitles.length > 0 && !filterTitles.includes(String(r.sessionId))) return false;
    return true;
  }), [seasonRows, filterTitles]);

  // ── Match History table sorting ─────────────────────────────────────────────
  // Defaults to the table's existing natural order (newest match first) so sorting is purely
  // additive — nothing changes until a header is clicked. Only affects table row order; the
  // chart/averages above keep reading filteredRows directly, unsorted.
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };
  // TD/HID/SD/HMLD/SA columns rank on whatever's currently displayed (respecting the
  // Volume/Per-Minute toggle), not always the raw volume figure — same value rowVal() renders.
  const sortableRows = useMemo(() => filteredRows.map(r => {
    const perMin = key => (r.mins > 0 ? r[key] / r.mins : null);
    return {
      ...r,
      td_sort: statMode === 'volume' ? r.td : perMin('td'),
      hid_sort: statMode === 'volume' ? r.hid : perMin('hid'),
      sd_sort: statMode === 'volume' ? r.sd : perMin('sd'),
      hmld_sort: statMode === 'volume' ? r.hmld : perMin('hmld'),
      sa_sort: statMode === 'volume' ? r.sa : perMin('sa'),
    };
  }), [filteredRows, statMode]);
  const sortedTableRows = useMemo(
    () => [...sortableRows].sort((a, b) => compareRows(a, b, sortKey, sortDir)),
    [sortableRows, sortKey, sortDir]
  );

  // ── Player card averages ─────────────────────────────────────────────────────
  const avgs = useMemo(() => {
    const n = filteredRows.length;
    if (!n) return null;
    // Null-aware (2026-10-24): a KNVB-estimated match without this metric is left out of its average.
    const avg = key => {
      const vals = filteredRows.map(r => r[key]).filter(v => v != null);
      return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    };
    return { mins: avg('mins'), td: avg('td'), hid: avg('hid'), sa: avg('sa') };
  }, [filteredRows]);

  // ── Bar chart — oldest left, newest right ──────────────────────────────────
  // Per-minute toggle affects the bar chart too (2026-07-25) — same per-match rate as the
  // table (this match's total / this match's minutes), not divided again by the average.
  // Rounded to 1 decimal instead of a whole number in that mode since per-minute values are
  // much smaller (single/low-double digits) and would otherwise flatten to indistinguishable
  // whole numbers. Computes every candidate metric up front (cheap) so switching
  // chartMetric — which parameter(s) get plotted, 2026-07-25 — doesn't need a second pass.
  const chartData = useMemo(() =>
    [...filteredRows]
      .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
      .map(r => {
        const perMin = statMode === 'perMin';
        const out = {
          label: r.opponent.split(' ').slice(0, 2).join(' '),
          date:  r.date ? `${r.date.slice(8)}-${r.date.slice(5, 7)}` : '',
        };
        CHART_METRIC_FIELDS.forEach(key => {
          const raw = perMin ? (r.mins > 0 ? (r[key] || 0) / r.mins : 0) : (r[key] || 0);
          const decimals = perMin ? CHART_METRIC_PERMIN_DECIMALS[key] : 0;
          out[key] = decimals ? Math.round(raw * 10 ** decimals) / 10 ** decimals : Math.round(raw);
        });
        return out;
      }), [filteredRows, statMode]);

  // ── Average row ──────────────────────────────────────────────────────────────
  const avgRow = useMemo(() => {
    const n = filteredRows.length;
    if (!n) return null;
    // Null-aware (2026-10-24): a KNVB-estimated match without this metric is left out of its average.
    const avg = key => {
      const vals = filteredRows.map(r => r[key]).filter(v => v != null);
      return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
    };
    return { td: avg('td'), hid: avg('hid'), sd: avg('sd'), hmld: avg('hmld'),
      sa: avg('sa'), expl: avg('expl'), maxSpd: avg('maxSpd'), mins: avg('mins'),
      tdPct: avg('tdPct'), hidPct: avg('hidPct'), sdPct: avg('sdPct'),
      hmldPct: avg('hmldPct'), saPct: avg('saPct') };
  }, [filteredRows]);

  // ── Per-minute average row — lets players with different avg minutes be compared ──
  const perMinRow = useMemo(() => computePerMinRow(filteredRows), [filteredRows]);

  // ── PDF export — open browser print page in new tab ─────────────────────────
  const handleStartExport = useCallback((playerIdList) => {
    setShowExportModal(false);
    if (!playerIdList.length) return;
    localStorage.setItem('print_players', JSON.stringify(playerIdList));
    window.open('/print/match-chronic', '_blank');
  }, []);

  // ── Shared styles ────────────────────────────────────────────────────────────
  const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14 };
  const TH   = { padding: '9px 10px', fontSize: '0.67rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-muted)', textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', background: 'var(--bg-app)' };
  const THL  = { ...TH, textAlign: 'left' };
  const TD_  = { padding: '9px 10px', fontSize: '0.82rem', color: 'var(--text-primary)', textAlign: 'right', borderBottom: '1px solid var(--border)' };
  const TDL  = { ...TD_, textAlign: 'left' };
  const GRN  = { background: 'rgba(16,185,129,0.10)', color: '#6ee7b7' };
  const SEL  = { padding: '7px 28px 7px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: '0.84rem', fontFamily: 'inherit', cursor: 'pointer', outline: 'none', appearance: 'none', WebkitAppearance: 'none' };

  // ── Volume vs per-minute display (2026-07-25) ───────────────────────────────
  // Per-row: divide that match's own total by that match's own minutes. Summary row: reuses
  // the existing sum(param)/sum(mins) computation (perMinRow) rather than averaging the
  // per-row rates — more statistically sound (a 10-minute cameo doesn't get equal weight to
  // a full 90), and it's the exact number the table used to show in its own dedicated row,
  // which this folds into the one summary row instead of showing twice.
  const COL_LABELS = { td: 'TD', hid: 'HID', sd: 'SD', hmld: 'HMLD', sa: 'SA' };
  const colLabel = key => statMode === 'perMin' ? `${COL_LABELS[key]}/Min` : COL_LABELS[key];
  // TD/min is a big-enough number (~100+ m/min) that decimals just add noise; HID/SD/HMLD/SA
  // per minute are small (single/low-double digits) and need the decimal to stay legible.
  const permin = (v) => v == null || isNaN(v) ? '—' : f1(v);
  const perMinFmt = { td: v => f0(v), hid: permin, sd: permin, hmld: permin, sa: permin };
  // KNVB-estimated matches (2026-10-24): marked value (interval inline for SD/HMLD, tooltip for
  // TD/HID), "niet geschat" for a metric without an estimate — never a 0.
  const rowVal = (r, key) => {
    if (statMode === 'volume') return <EstimatedValue value={r[key]} basis={r.estimate} metric={key} />;
    if (r.isEstimated && r[key] == null) return <EstimatedValue value={null} basis={r.estimate} metric={key} />;
    if (!(r.mins > 0) || r[key] == null) return '—';
    const v = perMinFmt[key](r[key] / r.mins);
    return r.isEstimated ? <>{v}<EstimatedMarker basis={r.estimate} metric={key} /></> : v;
  };
  const summaryVal = key => statMode === 'volume' ? f0(avgRow?.[key]) : (perMinRow ? perMinFmt[key](perMinRow[key]) : '—');
  const summaryLabel = statMode === 'volume' ? 'Season Average' : 'Season Average (per min)';

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-muted)', fontSize: '0.9rem' }}>Loading…</div>
  );

  const hasAnyData  = allMatchRows.length > 0;
  const hasData     = seasonRows.length > 0;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 40 }}>

      {/* ── Header + controls ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16 }}>
        <div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Match Chronic</h2>
          <p style={{ fontSize: '0.83rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
            {viewMode === 'team' ? 'Season match performance, team level' : 'Season match performance per player'}
          </p>
        </div>
        <img src={`${process.env.PUBLIC_URL}/heracles-crest.png`} alt="Heracles Almelo"
          style={{ height: 44, width: 'auto', opacity: 0.85 }} />
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        {!playerOverride && (
          <SegmentedToggle
            value={viewMode}
            onChange={v => { setViewMode(v); setFilterTitles([]); }}
            options={[{ value: 'individual', label: 'Individual' }, { value: 'team', label: 'Team' }]}
          />
        )}
        {!playerOverride && viewMode === 'individual' && (
          <select value={playerId} onChange={e => setPlayerId(e.target.value)} style={SEL}>
            {players.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
        )}
        <MultiSelect options={opponentOptions} selected={filterTitles} onChange={setFilterTitles} placeholder="All matches" />
        {filterTitles.length > 0 && (
          <button onClick={() => setFilterTitles([])}
            style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit' }}>
            Clear
          </button>
        )}
        <SegmentedToggle
          value={statMode}
          onChange={setStatMode}
          options={[{ value: 'volume', label: 'Volume' }, { value: 'perMin', label: 'Per Minute' }]}
        />
        {viewMode === 'individual' && (
          <div style={{ marginLeft: 'auto' }}>
            <button
              onClick={() => setShowPhysicalReportModal(true)}
              disabled={players.length === 0}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.83rem', fontWeight: 600, fontFamily: 'inherit' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              PDF Export
            </button>
          </div>
        )}
      </div>

      {/* ── Empty state ── */}
      {!hasAnyData && (
        <div style={{ ...card, padding: 48, textAlign: 'center', color: 'var(--text-dimmed)' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            {viewMode === 'team' ? 'No team match data found. Upload match CSVs in the Upload zone first.' : 'No match data found for this player. Upload match CSVs in the Upload zone first.'}
          </p>
        </div>
      )}
      {hasAnyData && !hasData && (
        <div style={{ ...card, padding: 48, textAlign: 'center', color: 'var(--text-dimmed)' }}>
          <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
            No data for this season. Change the season filter in Settings to view other data.
          </p>
        </div>
      )}

      {hasAnyData && hasData && (
        <>
          {/* ── Player card + bar chart — stacked ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>

            {/* Compact horizontal player/team card */}
            <div style={{ ...card, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              {/* Avatar + name (or crest + "Team" in Team mode) */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                {viewMode === 'team' ? (
                  <>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', flexShrink: 0, background: 'var(--bg-app)', border: '2px solid var(--border-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 6, boxSizing: 'border-box' }}>
                      <img src={`${process.env.PUBLIC_URL}/heracles-crest.png`} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                    <div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>Heracles Almelo</div>
                      <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'rgba(59,130,246,0.15)', color: '#60a5fa', letterSpacing: '0.06em' }}>
                        Squad average
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <Avatar name={player?.name} photoUrl={player?.photo_url} size={48} />
                    <div>
                      <div style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>{player?.name || '—'}</div>
                      {player?.position && (
                        <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: 'rgba(59,130,246,0.15)', color: '#60a5fa', letterSpacing: '0.06em' }}>
                          {player.position}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Divider */}
              <div style={{ width: 1, height: 40, background: 'var(--border-strong)', flexShrink: 0 }} />

              {/* Stats row */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', flex: 1 }}>
                {[
                  { label: 'Avg Mins',    value: avgs ? f1(avgs.mins)         : '—' },
                  { label: 'Avg TD',      value: avgs ? f0(avgs.td)  + ' m'   : '—' },
                  { label: 'Avg HID',     value: avgs ? f0(avgs.hid) + ' m'   : '—' },
                  { label: 'Avg Sh.Act',  value: avgs ? f0(avgs.sa)           : '—' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ background: 'var(--bg-app)', borderRadius: 8, padding: '8px 14px', minWidth: 90 }}>
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Match count badge */}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-dimmed)', flexShrink: 0 }}>
                {filteredRows.length} match{filteredRows.length !== 1 ? 'es' : ''}
              </div>
            </div>

            {/* Bar chart — now full width */}
            <div style={{ ...card, display: 'flex', flexDirection: 'column', padding: '20px 20px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
                <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {chartMetric === 'combo' ? 'Game Load' : CHART_METRIC_OPTIONS.find(o => o.value === chartMetric)?.label}
                  <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted)', marginLeft: 8 }}>{chartData.length} match{chartData.length !== 1 ? 'es' : ''}</span>
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  {chartMetric === 'combo' ? (
                    <div style={{ display: 'flex', gap: 14, fontSize: '0.71rem', color: 'var(--text-muted)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#38bdf8', display: 'inline-block' }} />HID &gt;19.8km/h{statMode === 'perMin' ? '/min' : ''}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#1e40af', display: 'inline-block' }} />Explosive Dist.{statMode === 'perMin' ? '/min' : ''}</span>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 14, fontSize: '0.71rem', color: 'var(--text-muted)' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 2, background: CHART_METRIC_COLORS[chartMetric], display: 'inline-block' }} />
                        {CHART_METRIC_OPTIONS.find(o => o.value === chartMetric)?.label}{statMode === 'perMin' ? '/min' : ''}
                      </span>
                    </div>
                  )}
                  <select value={chartMetric} onChange={e => setChartMetric(e.target.value)} style={{ ...SEL, padding: '5px 24px 5px 9px', fontSize: '0.75rem' }}>
                    {CHART_METRIC_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ height: 230 }}>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 32 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                      <XAxis dataKey="label"
                        tick={({ x, y, payload }) => {
                          const d = chartData.find(c => c.label === payload.value);
                          return (
                            <g transform={`translate(${x},${y + 6})`}>
                              <text textAnchor="middle" fill="var(--text-secondary)" fontSize={9} fontFamily="Inter, sans-serif" dy={0}>{payload.value}</text>
                              <text textAnchor="middle" fill="var(--text-dimmed)"    fontSize={8} fontFamily="Inter, sans-serif" dy={11}>{d?.date || ''}</text>
                            </g>
                          );
                        }}
                        axisLine={false} tickLine={false} interval={0} height={38} />
                      <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Inter, sans-serif' }}
                        axisLine={false} tickLine={false}
                        tickFormatter={v => {
                          if (statMode !== 'perMin') return v >= 1000 ? `${(v/1000).toFixed(1)}k` : v;
                          const decimals = chartMetric === 'combo' ? 1 : CHART_METRIC_PERMIN_DECIMALS[chartMetric];
                          return decimals ? v.toFixed(decimals) : Math.round(v);
                        }} />
                      {chartMetric === 'combo' ? (
                        <>
                          <Bar dataKey="hid"  stackId="a" fill="#38bdf8" radius={[0,0,3,3]} maxBarSize={52}>
                            <LabelList content={props => <BarLabel {...props} decimals={statMode === 'perMin' ? 1 : 0} />} />
                          </Bar>
                          <Bar dataKey="expl" stackId="a" fill="#1e40af" radius={[3,3,0,0]} maxBarSize={52}>
                            <LabelList content={props => <BarLabel {...props} decimals={statMode === 'perMin' ? 1 : 0} />} />
                          </Bar>
                        </>
                      ) : (
                        <Bar dataKey={chartMetric} fill={CHART_METRIC_COLORS[chartMetric]} radius={[3,3,0,0]} maxBarSize={52}>
                          <LabelList content={props => <BarLabel {...props} decimals={statMode === 'perMin' ? CHART_METRIC_PERMIN_DECIMALS[chartMetric] : 0} />} />
                        </Bar>
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-dimmed)', fontSize: '0.83rem' }}>No match data</div>
                )}
              </div>
            </div>
          </div>

          {/* ── Match history table ── */}
          <div style={{ ...card, overflow: 'auto' }}>
            <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>Match History</span>
              <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{filteredRows.length} matches</span>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
              <thead>
                <tr>
                  <SortTh sortKey="opponent" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={THL}>Opponent</SortTh>
                  <SortTh sortKey="date" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>Date</SortTh>
                  <SortTh sortKey="td_sort" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>{colLabel('td')}</SortTh>
                  <SortTh sortKey="tdPct" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>TD%</SortTh>
                  <SortTh sortKey="hid_sort" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>{colLabel('hid')}</SortTh>
                  <SortTh sortKey="hidPct" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>HID%</SortTh>
                  <SortTh sortKey="sd_sort" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>{colLabel('sd')}</SortTh>
                  <SortTh sortKey="sdPct" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>SD%</SortTh>
                  <SortTh sortKey="hmld_sort" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>{colLabel('hmld')}</SortTh>
                  <SortTh sortKey="hmldPct" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>HMLD%</SortTh>
                  <SortTh sortKey="sa_sort" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>{colLabel('sa')}</SortTh>
                  <SortTh sortKey="saPct" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>SA%</SortTh>
                  <SortTh sortKey="maxSpd" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>Max Spd</SortTh>
                  <SortTh sortKey="mins" activeKey={sortKey} dir={sortDir} onClick={toggleSort} style={TH}>Mins</SortTh>
                </tr>
              </thead>
              <tbody>
                {sortedTableRows.length === 0 ? (
                  <tr><td colSpan={14} style={{ ...TD_, textAlign: 'center', color: 'var(--text-dimmed)', padding: 32 }}>No matches match the current filters</td></tr>
                ) : sortedTableRows.map((r, i) => (
                  <tr key={r.sessionId} style={{ background: i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent' }}>
                    <td style={TDL}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <OppLogo name={r.opponent} logoUrl={r.oppLogo} size={20} />
                        <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>{r.opponent}</span>
                        {r.isEstimated && <EstimatedMarker basis={r.estimate} />}
                      </div>
                    </td>
                    <td style={{ ...TD_, whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{fmtDate(r.date)}</td>
                    <td style={{ ...TD_, ...(r.tdGreen   ? GRN : {}) }}>{rowVal(r, 'td')}</td>
                    <td style={{ ...TD_, ...(r.tdGreen   ? GRN : {}) }}>{fPct(r.tdPct)}</td>
                    <td style={{ ...TD_, ...(r.hidGreen  ? GRN : {}) }}>{rowVal(r, 'hid')}</td>
                    <td style={{ ...TD_, ...(r.hidGreen  ? GRN : {}) }}>{fPct(r.hidPct)}</td>
                    <td style={{ ...TD_, ...(r.sdGreen   ? GRN : {}) }}>{rowVal(r, 'sd')}</td>
                    <td style={{ ...TD_, ...(r.sdGreen   ? GRN : {}) }}>{fPct(r.sdPct)}</td>
                    <td style={{ ...TD_, ...(r.hmldGreen ? GRN : {}) }}>{rowVal(r, 'hmld')}</td>
                    <td style={{ ...TD_, ...(r.hmldGreen ? GRN : {}) }}>{fPct(r.hmldPct)}</td>
                    <td style={{ ...TD_, ...(r.saGreen   ? GRN : {}) }}>{rowVal(r, 'sa')}</td>
                    <td style={{ ...TD_, ...(r.saGreen   ? GRN : {}) }}>{fPct(r.saPct)}</td>
                    <td style={TD_}><EstimatedValue value={r.maxSpd} basis={r.estimate} metric="max_speed" format={v => v.toFixed(1)} /></td>
                    <td style={TD_}>{f1(r.mins)}</td>
                  </tr>
                ))}
                {avgRow && (
                  <tr style={{ background: 'rgba(59,130,246,0.05)', borderTop: '2px solid var(--border-strong)' }}>
                    <td style={{ ...TDL, fontWeight: 700, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{summaryLabel}</td>
                    <td style={TD_}>—</td>
                    <td style={{ ...TD_, fontWeight: 700 }}>{summaryVal('td')}</td>
                    <td style={TD_}>{fPct(avgRow.tdPct)}</td>
                    <td style={{ ...TD_, fontWeight: 700 }}>{summaryVal('hid')}</td>
                    <td style={TD_}>{fPct(avgRow.hidPct)}</td>
                    <td style={{ ...TD_, fontWeight: 700 }}>{summaryVal('sd')}</td>
                    <td style={TD_}>{fPct(avgRow.sdPct)}</td>
                    <td style={{ ...TD_, fontWeight: 700 }}>{summaryVal('hmld')}</td>
                    <td style={TD_}>{fPct(avgRow.hmldPct)}</td>
                    <td style={{ ...TD_, fontWeight: 700 }}>{summaryVal('sa')}</td>
                    <td style={TD_}>{fPct(avgRow.saPct)}</td>
                    <td style={TD_}>{f1(avgRow.maxSpd)}</td>
                    <td style={TD_}>{f1(avgRow.mins)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Modal ── */}
      {showExportModal && (
        <ExportModal players={players.filter(p => p.position !== 'GK')} onExport={handleStartExport} onClose={() => setShowExportModal(false)} />
      )}
      {showPhysicalReportModal && player && (
        <PhysicalReportExportModal
          currentPlayer={player}
          players={players}
          opponents={opponents}
          scenarios={scenarios}
          customTemplates={customTemplates}
          positions={positions}
          allMatchDrills={allMatchDrills}
          matchOptions={opponentOptions}
          initialMatchIds={filterTitles}
          legacyDescription={playerOverride
            ? `Match Chronic for ${player.name}, as shown today — always includes every match, the match filter below only applies to Full Physical Report.`
            : 'Pick which players to include (opens the existing player picker) — always includes every match for each player.'}
          onLegacyExport={() => playerOverride ? handleStartExport([String(playerOverride.id)]) : setShowExportModal(true)}
          onClose={() => setShowPhysicalReportModal(false)}
        />
      )}
    </div>
  );
}
